/**
 * Utility functions for handling subscription pricing
 */

/**
 * Calculate the subscription price based on selling plan adjustments
 * @param {number} basePrice - The base variant price
 * @param {object} priceAdjustment - The selling plan price adjustment
 * @returns {number} The adjusted price
 */
export function calculateSubscriptionPrice(basePrice, priceAdjustment) {
  if (!priceAdjustment?.adjustmentValue) return basePrice;

  const adjustmentValue = priceAdjustment.adjustmentValue;

  // Fixed price adjustment (sets the price to a specific amount)
  if (adjustmentValue.price?.amount) {
    return parseFloat(adjustmentValue.price.amount);
  }

  // Fixed amount adjustment (subtracts a fixed amount)
  if (adjustmentValue.adjustmentAmount?.amount) {
    const discount = parseFloat(adjustmentValue.adjustmentAmount.amount);
    return Math.max(0, basePrice - discount);
  }

  // Percentage adjustment
  if (adjustmentValue.adjustmentPercentage !== undefined) {
    // Handle both positive and negative percentage values
    // Positive percentage = discount (e.g., 10 = 10% off)
    // Negative percentage = surcharge (e.g., -10 = 10% extra)
    const percentChange = adjustmentValue.adjustmentPercentage / 100;
    return basePrice * (1 - percentChange);
  }

  return basePrice;
}

/**
 * Get the price adjustment for a specific selling plan
 * @param {object} sellingPlan - The selling plan object
 * @param {number} orderCount - The order count (default 1 for first order)
 * @returns {object|null} The price adjustment or null
 */
export function getPriceAdjustment(sellingPlan, orderCount = 1) {
  if (!sellingPlan?.priceAdjustments) return null;

  // Find adjustment for the specific order count
  const adjustment = sellingPlan.priceAdjustments.find(
    adj => adj.orderCount === orderCount || adj.orderCount === null
  );

  return adjustment || sellingPlan.priceAdjustments[0] || null;
}

/**
 * Format subscription pricing display
 * @param {number} originalPrice - Original price
 * @param {number} subscriptionPrice - Subscription price
 * @returns {object} Formatted pricing info
 */
export function formatSubscriptionPricing(originalPrice, subscriptionPrice) {
  const savings = originalPrice - subscriptionPrice;
  const savingsPercent = (savings / originalPrice) * 100;

  return {
    originalPrice: originalPrice.toFixed(2),
    subscriptionPrice: subscriptionPrice.toFixed(2),
    savings: savings.toFixed(2),
    savingsPercent: Math.round(savingsPercent),
    hasDiscount: savings > 0
  };
}

/**
 * Get subscription price from cart line with selling plan
 * @param {object} cartLine - Cart line with cost and selling plan data
 * @returns {number} The subscription price per unit
 */
export function getSubscriptionPriceFromCartLine(cartLine) {
  // First check if we have cost data from the cart API
  if (cartLine.cost?.amountPerQuantity?.amount) {
    return parseFloat(cartLine.cost.amountPerQuantity.amount);
  }

  // Fallback to selling plan price adjustments
  if (cartLine.sellingPlanAllocation?.priceAdjustments?.price?.amount) {
    return parseFloat(cartLine.sellingPlanAllocation.priceAdjustments.price.amount);
  }

  // Final fallback to merchandise price
  return parseFloat(cartLine.merchandise?.price?.amount || 0);
}

/**
 * Get the compare at price for subscription items
 * @param {object} cartLine - Cart line with cost and selling plan data
 * @returns {number|null} The compare at price or null
 */
export function getSubscriptionComparePrice(cartLine) {
  // Check cart line cost data first
  if (cartLine.cost?.compareAtAmountPerQuantity?.amount) {
    return parseFloat(cartLine.cost.compareAtAmountPerQuantity.amount);
  }

  // Check selling plan allocation
  if (cartLine.sellingPlanAllocation?.priceAdjustments?.compareAtPrice?.amount) {
    return parseFloat(cartLine.sellingPlanAllocation.priceAdjustments.compareAtPrice.amount);
  }

  // Check merchandise compare at price
  if (cartLine.merchandise?.compareAtPrice?.amount) {
    return parseFloat(cartLine.merchandise.compareAtPrice.amount);
  }

  // Use regular price as compare price for subscriptions
  if (cartLine.sellingPlanAllocation && cartLine.merchandise?.price?.amount) {
    return parseFloat(cartLine.merchandise.price.amount);
  }

  return null;
}